import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Alert from "../../components/common/Alert";
import Button from "../../components/common/Button";
import Card from "../../components/common/Card";
import Checkbox from "../../components/common/Checkbox";
import Input from "../../components/common/Input";
import PageHeader from "../../components/common/PageHeader";
import Toast from "../../components/common/Toast";
import {
  useConfigureEdgeStoreMutation,
  useGetEdgeStoreSetupQuery,
} from "../../store/api";
import {
  getDefaultEdgeNodeName,
  persistEdgeStoreSetup,
  readEdgeStoreSetup,
} from "../../utils/edgeStoreSetup";

const getInitialForm = (storedSetup) => ({
  storeId: storedSetup?.storeId || "",
  nodeName: storedSetup?.nodeName || getDefaultEdgeNodeName(storedSetup?.storeSlug),
  triggerInitialSync: true,
});

const formatTimestamp = (value) => {
  if (!value) return "Not available";

  const timestamp = new Date(value);
  return Number.isNaN(timestamp.getTime())
    ? "Not available"
    : timestamp.toLocaleString();
};

const formatAddress = (setup) => {
  const parts = [
    setup?.addressLine1,
    setup?.addressLine2,
    setup?.city,
    setup?.state,
    setup?.postalCode,
  ].filter(Boolean);

  return parts.length > 0 ? parts.join(", ") : "Not available";
};

const getSetupErrorMessage = (error, fallbackMessage) => {
  const validationErrors = error?.data?.errors;

  if (Array.isArray(validationErrors) && validationErrors.length > 0) {
    return validationErrors
      .map((entry) => entry?.message || entry)
      .filter(Boolean)
      .join(" ");
  }

  if (validationErrors && typeof validationErrors === "object") {
    const fieldMessages = Object.values(validationErrors)
      .flat()
      .filter(Boolean);
    if (fieldMessages.length > 0) {
      return fieldMessages.join(" ");
    }
  }

  return (
    error?.data?.detail ||
    error?.data?.message ||
    error?.error ||
    fallbackMessage
  );
};

const ConfigureStore = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [toast, setToast] = useState(null);
  const [localError, setLocalError] = useState("");
  const [syncStarted, setSyncStarted] = useState(false);
  const [form, setForm] = useState(() => getInitialForm(readEdgeStoreSetup()));
  const {
    data: setupStatus,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetEdgeStoreSetupQuery();
  const [configureStore, { isLoading: isSubmitting }] =
    useConfigureEdgeStoreMutation();

  const isSetupRoute = location.pathname === "/setup/store";
  const isReconfigureRoute =
    location.pathname === "/settings/configure-store/reconfigure";
  const configuredSetup = setupStatus?.isConfigured ? setupStatus : null;
  const nextRoute =
    location.state?.from?.pathname &&
    location.state.from.pathname !== "/setup/store"
      ? location.state.from.pathname
      : "/login";

  useEffect(() => {
    if (!configuredSetup) return;

    persistEdgeStoreSetup(configuredSetup);
    setForm((prev) => ({
      ...prev,
      storeId: prev.storeId || configuredSetup.storeId || "",
      nodeName:
        prev.nodeName ||
        configuredSetup.nodeName ||
        getDefaultEdgeNodeName(configuredSetup.storeSlug),
    }));
  }, [configuredSetup]);

  const derivedNodeName = useMemo(() => {
    if (form.nodeName.trim()) return "";

    return getDefaultEdgeNodeName(
      configuredSetup?.storeSlug || readEdgeStoreSetup()?.storeSlug,
    );
  }, [configuredSetup?.storeSlug, form.nodeName]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    const formKey = name === "edgeNodeName" ? "nodeName" : name;
    setForm((prev) => ({
      ...prev,
      [formKey]: type === "checkbox" ? checked : value,
    }));
    setLocalError("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const storeId = form.storeId.trim();
    const nodeName = form.nodeName.trim();

    if (!storeId) {
      setLocalError("Store ID is required.");
      return;
    }

    setLocalError("");
    setSyncStarted(Boolean(form.triggerInitialSync));

    try {
      const response = await configureStore({
        storeId,
        nodeName: nodeName || undefined,
        triggerInitialSync: Boolean(form.triggerInitialSync),
      }).unwrap();

      persistEdgeStoreSetup(response);
      setToast({
        id: Date.now(),
        tone: "success",
        message: response?.initialSyncCompleted
          ? "Store configured on Edge and initial sync completed."
          : "Store configured on Edge successfully.",
      });

      setTimeout(() => {
        navigate(
          isReconfigureRoute ? "/settings/configure-store" : nextRoute,
          { replace: true },
        );
      }, 800);
    } catch (err) {
      setSyncStarted(false);
      setLocalError(
        getSetupErrorMessage(err, "Failed to configure the store on Edge."),
      );
    }
  };

  const handleSyncNow = async () => {
    const storeId = (configuredSetup?.storeId || form.storeId || "").trim();
    const nodeName = (configuredSetup?.nodeName || form.nodeName || "").trim();

    if (!storeId) {
      setLocalError("Store ID is required to start sync.");
      return;
    }

    setLocalError("");
    setSyncStarted(true);

    try {
      const response = await configureStore({
        storeId,
        nodeName: nodeName || undefined,
        triggerInitialSync: true,
      }).unwrap();

      persistEdgeStoreSetup(response);
      setToast({
        id: Date.now(),
        tone: "success",
        message: "Store sync started. Edge is pulling latest master data.",
      });
      refetch();
    } catch (err) {
      setSyncStarted(false);
      setLocalError(getSetupErrorMessage(err, "Failed to start store sync."));
    }
  };

  const statusCards = [
    { label: "Store Name", value: configuredSetup?.storeName || "Not configured" },
    { label: "Address", value: formatAddress(configuredSetup) },
    { label: "GST Number", value: configuredSetup?.gstNumber || "Not configured" },
    { label: "Phone", value: configuredSetup?.phone || "Not configured" },
    { label: "Email", value: configuredSetup?.email || "Not configured" },
    { label: "Store Slug", value: configuredSetup?.storeSlug || "Not configured" },
    { label: "Timezone", value: configuredSetup?.timezone || "Not configured" },
    { label: "Currency", value: configuredSetup?.currencyCode || "Not configured" },
    {
      label: "Last Master Token",
      value:
        configuredSetup?.lastMasterToken === null ||
        configuredSetup?.lastMasterToken === undefined
          ? "Not available"
          : String(configuredSetup.lastMasterToken),
    },
    { label: "Updated At", value: formatTimestamp(configuredSetup?.updatedAt) },
  ];

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4">
        <Card className="max-w-lg text-center">
          <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-indigo-600 dark:border-slate-600 dark:border-t-indigo-400" />
          <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            Loading setup status
          </h1>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
            Checking whether this Edge instance already has an active store.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {toast && (
        <Toast
          message={toast.message}
          tone={toast.tone}
          onClose={() => setToast(null)}
        />
      )}

      <PageHeader
        title={
          isSetupRoute
            ? "Configure Store"
            : isReconfigureRoute
              ? "Reconfigure Store"
              : "Store Configuration"
        }
        subtitle={
          isSetupRoute
            ? "Set the active store for this Edge instance before normal usage starts."
            : isReconfigureRoute
              ? "Edit the active store configuration for this Edge instance and save the update."
              : "View the active store for this Edge instance."
        }
        actions={
          // Reconfigure entry button is hidden for now; keep route and flow for future use.
          configuredSetup ? (
            <div className="flex flex-wrap items-center gap-3">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => refetch()}
                disabled={isSubmitting}
              >
                Refresh Status
              </Button>
              {!isSetupRoute && !isReconfigureRoute && (
                <Button size="sm" onClick={handleSyncNow} disabled={isSubmitting}>
                  {isSubmitting ? "Syncing..." : "Sync Store"}
                </Button>
              )}
              {isReconfigureRoute && (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => navigate("/settings/configure-store")}
                >
                  Back
                </Button>
              )}
            </div>
          ) : null
        }
      />

      {isError && (
        <Card>
          <Alert tone="error">
            {getSetupErrorMessage(
              error,
              "Failed to load Edge store setup status.",
            )}
          </Alert>
          <div className="mt-4">
            <Button size="sm" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        </Card>
      )}

      {!isError && configuredSetup && !isSetupRoute && !isReconfigureRoute && (
        <Card>
          <div>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              Current setup status
            </h2>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
              This Edge node is configured and can resolve store context from the
              local Edge database.
            </p>
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-2">
            {statusCards.map((item) => (
              <div
                key={item.label}
                className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-800"
              >
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  {item.label}
                </p>
                <p className="mt-1 text-sm font-medium text-slate-900 dark:text-slate-100">
                  {item.value}
                </p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {!isError && (!configuredSetup || isSetupRoute || isReconfigureRoute) && (
        <Card>
          <form className="space-y-5" onSubmit={handleSubmit}>
            <div>
              <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                {configuredSetup
                  ? "Edit active store configuration"
                  : "Store not configured"}
              </h2>
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                {configuredSetup
                  ? "Update the editable Edge store settings below. Store details shown on the previous page come from the Edge backend response."
                  : "Configure the active store for this Edge instance using a store ID from Central."}
              </p>
            </div>

            {configuredSetup && isReconfigureRoute && (
              <div className="grid gap-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-4 dark:border-slate-700 dark:bg-slate-800 md:grid-cols-2">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    Current Store Name
                  </p>
                  <p className="mt-1 text-sm font-medium text-slate-900 dark:text-slate-100">
                    {configuredSetup.storeName || "Not available"}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    Current Store Slug
                  </p>
                  <p className="mt-1 text-sm font-medium text-slate-900 dark:text-slate-100">
                    {configuredSetup.storeSlug || "Not available"}
                  </p>
                </div>
              </div>
            )}

            {!configuredSetup && (
              <Alert tone="info">
                Normal Edge flows remain blocked until this setup is completed.
              </Alert>
            )}

            {syncStarted && isSubmitting && (
              <Alert tone="info">
                Initial sync in progress. Edge is configuring the active store and
                pulling master data.
              </Alert>
            )}

            {localError && <Alert tone="error">{localError}</Alert>}

            <div className="grid gap-4 md:grid-cols-2">
              <Input
                label="Store ID"
                name="storeId"
                value={form.storeId}
                onChange={handleChange}
                placeholder="CENTRAL_STORE_GUID"
                required
                disabled={isSubmitting}
                helper="Required. Use the store ID from Central."
              />
              <Input
                label="Edge Node Name (optional)"
                name="edgeNodeName"
                value={form.nodeName}
                onChange={handleChange}
                placeholder={derivedNodeName || "EDGE-MY-STORE"}
                disabled={isSubmitting}
                helper={
                  derivedNodeName
                    ? `If left blank, a readable name like ${derivedNodeName} can be used.`
                    : "Optional. Backend can generate this automatically if left blank."
                }
              />
            </div>

            <Checkbox
              label="Sync store data now"
              name="triggerInitialSync"
              checked={form.triggerInitialSync}
              onChange={handleChange}
              helper="Recommended. Edge will immediately pull catalog and master data after setup."
            />

            {configuredSetup && (
              <Alert tone="warning">
                Configuring a different store makes it the active store for this
                Edge instance.
              </Alert>
            )}

            <div className="flex flex-wrap gap-3">
              <Button type="submit">
                {isSubmitting
                  ? configuredSetup
                    ? "Updating..."
                    : "Configuring..."
                  : configuredSetup
                    ? "Update Store"
                    : "Configure Store"}
              </Button>
              {configuredSetup && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleSyncNow}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Syncing..." : "Sync Store"}
                </Button>
              )}
              <Button
                type="button"
                variant="secondary"
                onClick={() => refetch()}
                disabled={isSubmitting}
              >
                Refresh Status
              </Button>
            </div>
          </form>
        </Card>
      )}
    </div>
  );
};

export default ConfigureStore;
